/**
 * Console navigation model — one source of truth for the sidebar, the mobile
 * drawer and the command palette: grouped nav items with per-group accent
 * colors (point 12), icon names from the elevation icon set, and the palette's
 * quick actions (point 14).
 */
import type { Localized } from '@servicesai/ui';
import type { IconName } from '../components/elevated/icons.js';
import { SECTION_ACCENTS } from './accents.js';
import { S } from '../strings.js';

export interface ConsoleNavItem {
  to: string;
  label: Localized;
  icon: IconName;
  /** Palette hint line (not rendered in the sidebar). */
  hint?: Localized;
  /** Extra palette search terms (English). */
  keywords?: string;
  roles?: string[];
  featureFlag?: string;
}

export interface ConsoleNavGroup {
  id: 'executive' | 'operations' | 'engagement' | 'money' | 'platform';
  label: Localized;
  /** Section accent from the registry — tints the eyebrow + active trail. */
  accent: string;
  items: ConsoleNavItem[];
}

export const NAV_GROUPS: ConsoleNavGroup[] = [
  {
    id: 'executive',
    label: { en: 'Executive', ar: 'التنفيذي' },
    accent: SECTION_ACCENTS.executive,
    items: [
      {
        to: '/executive',
        label: { en: 'Executive Dashboard', ar: 'لوحة القيادة التنفيذية' },
        icon: 'chart',
        hint: { en: 'Services BI — animated, bilingual, AI-grounded', ar: 'لوحة خدمات الوزارة — متحركة وثنائية اللغة وبالذكاء الاصطناعي' },
        keywords: 'executive powerbi analytics insights forecast completion voc',
        featureFlag: 'executive',
      },
    ],
  },
  {
    id: 'operations',
    label: S.navOperations,
    accent: SECTION_ACCENTS.operations,
    items: [
      {
        to: '/dashboard',
        label: S.dashboard,
        icon: 'dashboard',
        hint: { en: 'Platform health and SLA', ar: 'صحة المنصة واتفاقيات الخدمة' },
        keywords: 'metrics sla overview',
      },
      {
        to: '/queues',
        label: S.queues,
        icon: 'queue',
        hint: { en: 'Cases ordered by SLA', ar: 'الحالات مرتبة حسب الموعد' },
        keywords: 'cases applications review',
        roles: ['reviewer', 'approver', 'auditor'],
      },
      {
        // Post-approval delivery. Named for what it is rather than "Manzili
        // governance", because that label is where somebody would go looking for
        // case review and they would find a delivery workspace.
        to: '/housing',
        label: { en: 'Manzili delivery', ar: 'تنفيذ منزلي' },
        icon: 'housing',
        hint: {
          en: 'Post-approval bundles, settings and trace',
          ar: 'باقات ما بعد الموافقة والإعدادات والتتبع',
        },
        keywords: 'manzili szhp bundle housing delivery post-approval',
        roles: ['reviewer', 'approver', 'auditor', 'admin'],
      },
      // Pre-approval review. These have a better home in the Housing Management
      // Console at /housing/ on this host, and they will hand over to it — but
      // they stay reachable here until somebody has signed in to that console in
      // production, because its staff SSO depends on a redirect URI registered in
      // the Keycloak realm rather than on anything in this repository. Removing
      // them in the same release that first deploys it would bet a reviewer's
      // only route to a case queue on configuration nothing here can check.
      {
        to: '/field-research',
        label: { en: 'Field research', ar: 'البحث الميداني' },
        icon: 'queue',
        hint: { en: 'Social and technical visit queues', ar: 'قوائم الزيارات الاجتماعية والفنية' },
        keywords: 'field research social technical visit investigation',
        roles: ['reviewer', 'approver', 'auditor', 'admin'],
      },
      {
        to: '/housing/committee',
        label: { en: 'Committee session', ar: 'جلسة اللجنة' },
        icon: 'approvals',
        hint: { en: 'Ranked cases awaiting an award', ar: 'حالات مرتبة بانتظار قرار' },
        keywords: 'committee award grant loan decision housing session',
        roles: ['approver', 'admin'],
      },
      {
        to: '/housing/financing-contracts',
        label: { en: 'Financing contracts', ar: 'عقود التمويل' },
        icon: 'housing',
        hint: { en: 'Signed packages to approve', ar: 'حزم موقعة للموافقة' },
        keywords: 'financing contract consultant signature ministry decision',
        roles: ['reviewer', 'approver', 'admin'],
      },
      {
        to: '/approvals',
        label: S.approvals,
        icon: 'approvals',
        hint: { en: 'Maker-checker requests', ar: 'طلبات الصانع والمدقق' },
        keywords: 'publish decision maker checker',
        roles: ['approver', 'service_approver', 'auditor'],
      },
      {
        to: '/documents',
        label: { en: 'Documents', ar: 'المستندات' },
        icon: 'documents',
        hint: { en: 'Vault evidence queue', ar: 'قائمة أدلة الخزنة' },
        keywords: 'vault evidence extraction',
        roles: ['reviewer', 'approver', 'auditor', 'admin'],
        featureFlag: 'documents',
      },
    ],
  },
  {
    id: 'engagement',
    label: S.navEngagement,
    accent: SECTION_ACCENTS.engagement,
    items: [
      {
        to: '/customers',
        label: S.customers,
        icon: 'customers',
        hint: { en: 'Search profiles and history', ar: 'البحث في الملفات والسجل' },
        keywords: 'crm profile phone emirates id',
        roles: ['reviewer', 'approver', 'auditor', 'admin'],
      },
      {
        to: '/crm',
        label: { en: 'Ministry CRM', ar: 'إدارة علاقات المتعاملين' },
        icon: 'integrations',
        hint: { en: 'Dynamics 365 — read-only, Microsoft sign-in', ar: 'دايناميكس 365 — قراءة فقط بدخول مايكروسوفت' },
        keywords: 'dynamics dataverse complaints suggestions appreciations grievances microsoft crm',
        roles: ['reviewer', 'approver', 'auditor', 'admin'],
        featureFlag: 'crm',
      },
      {
        to: '/handover',
        label: S.handover,
        icon: 'handover',
        hint: { en: 'Live citizen conversations', ar: 'محادثات المتعاملين المباشرة' },
        keywords: 'handover agent desk live chat queue escalation waiting human cxone',
        roles: ['agent', 'reviewer', 'approver', 'admin'],
      },
      {
        to: '/proactive',
        label: S.proactive,
        icon: 'proactive',
        hint: { en: 'Renewal reminders', ar: 'تذكيرات التجديد' },
        keywords: 'renewal outreach permits',
        roles: ['reviewer', 'approver', 'admin'],
      },
    ],
  },
  {
    id: 'money',
    label: { en: 'Money & sync', ar: 'المدفوعات والربط' },
    accent: SECTION_ACCENTS.money,
    items: [
      {
        to: '/payments',
        label: { en: 'Payments', ar: 'المدفوعات' },
        icon: 'payments',
        hint: { en: 'Intents and refunds', ar: 'طلبات الدفع والاسترداد' },
        keywords: 'magnati finance ledger reconciliation',
        roles: ['finance', 'approver', 'auditor', 'admin'],
        featureFlag: 'payments',
      },
      {
        to: '/govsync',
        label: { en: 'Government sync', ar: 'الربط الحكومي' },
        icon: 'govsync',
        hint: { en: 'Outbox and dead letters', ar: 'صندوق الصادر والرسائل المتعثرة' },
        keywords: 'mls integration retry outbox',
        roles: ['admin', 'auditor', 'approver'],
        featureFlag: 'govsync',
      },
    ],
  },
  {
    id: 'platform',
    label: S.navPlatform,
    accent: SECTION_ACCENTS.platform,
    items: [
      {
        to: '/services',
        label: S.services,
        icon: 'services',
        hint: { en: 'Visual Service Studio', ar: 'استوديو الخدمات المرئي' },
        keywords: 'builder studio catalog fields integrations',
        roles: ['service_editor', 'service_approver', 'reviewer', 'approver', 'auditor'],
      },
      {
        to: '/bundles',
        label: { en: 'Service bundles', ar: 'باقات الخدمات' },
        icon: 'services',
        hint: { en: 'Curated front doors onto groups of services', ar: 'واجهات منسّقة لمجموعات الخدمات' },
        keywords: 'bundle maritime manzili mortgage landing page composer sections',
        roles: ['service_editor', 'service_approver', 'reviewer', 'approver', 'auditor'],
      },
      {
        to: '/agents',
        label: S.agents,
        icon: 'agents',
        hint: { en: 'Portfolio, prompts, integrations and governance', ar: 'المحفظة والموجهات والتكاملات والحوكمة' },
        keywords: 'ai agent workspace prompt notes automations governance',
      },
      {
        to: '/integrations',
        label: { en: 'Integrations', ar: 'التكاملات' },
        icon: 'integrations',
        hint: { en: 'Dependencies and health', ar: 'الاعتماديات والحالة' },
        keywords: 'mls twilio cxone magnati uaepass',
        roles: ['admin', 'auditor', 'service_editor', 'service_approver'],
        featureFlag: 'integrations',
      },
      {
        to: '/notifications',
        label: { en: 'Notifications', ar: 'الإشعارات' },
        icon: 'notifications',
        hint: { en: 'Provider approvals and delivery', ar: 'اعتمادات المزود والتسليم' },
        keywords: 'twilio content templates',
        roles: ['admin', 'auditor', 'approver'],
        featureFlag: 'notifications',
      },
      {
        to: '/journeys',
        label: { en: 'Journey runner', ar: 'مشغّل الرحلات' },
        icon: 'agents',
        hint: { en: 'Run a citizen journey end to end', ar: 'شغّل رحلة متعامل كاملة' },
        keywords: 'journey test assurance smoke end to end rehearsal',
        roles: ['journey:run', 'admin'],
      },
      {
        to: '/audit',
        label: { en: 'Audit explorer', ar: 'مستكشف التدقيق' },
        icon: 'audit',
        hint: { en: 'Chain and access logs', ar: 'السلسلة وسجلات الوصول' },
        keywords: 'pdpl chain security access',
        roles: ['admin', 'auditor'],
      },
      {
        to: '/traces',
        label: { en: 'Conversation traces', ar: 'تتبّع المحادثات' },
        icon: 'audit',
        hint: { en: 'Prompts, plans and ministry payloads', ar: 'الموجّهات والخطط ومحتويات الوزارة' },
        keywords: 'trace tester uat prompt plan payload replay debug',
        roles: ['admin', 'auditor'],
      },
      {
        to: '/admin',
        label: { en: 'Administration', ar: 'الإدارة' },
        icon: 'admin',
        hint: { en: 'Users, settings and break-glass', ar: 'المستخدمون والإعدادات والطوارئ' },
        keywords: 'roles authority settings',
        roles: ['admin', 'auditor'],
      },
      {
        to: '/integrations/cxone',
        label: S.cxone,
        icon: 'cxone',
        hint: { en: 'Contact center settings', ar: 'إعدادات مركز التواصل' },
        keywords: 'nice cxone voice chat',
        roles: ['admin'],
      },
    ],
  },
];

export interface ConsoleQuickAction {
  id: string;
  label: Localized;
  hint: Localized;
  to: string;
  icon: IconName;
  keywords: string;
  roles?: string[];
}

/** Palette quick actions (point 14b) — verb-first shortcuts into workflows. */
export const QUICK_ACTIONS: ConsoleQuickAction[] = [
  {
    id: 'go-queues',
    label: { en: 'Go to queues', ar: 'الانتقال إلى قوائم المراجعة' },
    hint: { en: 'Open the review queues', ar: 'فتح قوائم المراجعة' },
    to: '/queues',
    icon: 'queue',
    keywords: 'go open cases review',
    roles: ['reviewer', 'approver', 'auditor'],
  },
  {
    id: 'open-housing',
    label: { en: 'Open Manzili delivery', ar: 'فتح تنفيذ منزلي' },
    hint: { en: 'Manzili bundles and settings', ar: 'باقات منزلي وإعداداتها' },
    to: '/housing',
    icon: 'housing',
    keywords: 'open manzili szhp bundle delivery',
    roles: ['reviewer', 'approver', 'auditor', 'admin'],
  },
  {
    id: 'verify-audit',
    label: { en: 'Verify audit chain', ar: 'التحقق من سلسلة التدقيق' },
    hint: { en: 'Open the audit explorer', ar: 'فتح مستكشف التدقيق' },
    to: '/audit',
    icon: 'shield',
    keywords: 'verify chain hash integrity pdpl',
    roles: ['admin', 'auditor'],
  },
];
